import { nullishDefault } from '../helpers/nullishDefault';
import { AssignedLabel, ResolvedLabel } from '../label/itemLabel';
import { ProductNutritionInfo } from '../nutrition/nutritionTypes';
import { OtherString } from '../otherString';
import { Timestamped } from '../timestamped';
import { z } from 'zod';

export const SearchFlag = z.enum(['isPlu', 'isFruitOrVeggie', 'isBakery', 'isDrink', 'isBundle']);
export type SearchFlag = z.infer<typeof SearchFlag>;

export const FoodFlag = z.enum([
    'lactoseFree',
    'glutenFree',
    'organic',
    'vegan',
    'vegetarian',
    'diet',
    'regional',
    'local',
    'permSale',
    'gentech',
]);
export type FoodFlag = z.infer<typeof FoodFlag>;

export const OtherFlag = z.enum([
    'hasImage',
    'heavy',
    'light',
    'weightProduct',
    'singlePosTransferOnly',
    'noBonusPoints',
    'notDiscountable',
    'negative',
    'collectable',
    'storeBrand',
    'quantityInputRequired',
]);
export type OtherFlag = z.infer<typeof OtherFlag>;

export const Flag = z.enum([...SearchFlag.options, ...FoodFlag.options, ...OtherFlag.options]);

export type Flag = z.infer<typeof Flag>;

export const Price = z.object({
    price: z.coerce.number(),
    priceint: z.number(),
    validFrom: z.coerce.date(),
    validTo: z.coerce.date(),
    /** 00 -> normal price, 01 -> action price */
    priceTC: z.string(),
    discount: z.number().nullish(),
});
export type Price = z.infer<typeof Price>;

export const AssignedShelf = z.object({
    shelf: z.number().nullish(),
    shelfId: z.string().nullish(),
    systemId: z.number().nullish(),
    x: nullishDefault(z.coerce.number(), 0),
    y: nullishDefault(z.coerce.number(), 0),
});

export type AssignedShelf = z.infer<typeof AssignedShelf>;

export const DisableAddLocalKey = z.enum(['needsScan', 'phoneOrGiftcard', 'priceRequired', 'weightProduct']).or(z.string());
export type DisableAddLocalKey = z.infer<typeof DisableAddLocalKey>;

export const UiProduct = Timestamped.extend({
    _id: z.string().optional(),
    itemId: z.string(),
    rootItemId: z.string(),
    brand: z.string().nullish(),
    name: z.string(),
    storeGuid: z.string(),
    gtins: z.array(z.string()),
    gtin: z.string().nullish(),
    /** @deprecated use cgHierarchy */
    cgId: z.string().nullish(),
    /** @deprecated use cgHierarchy */
    cgLongId: z.string().nullish(),
    prices: z.array(Price),
    contents: z.string().nullish(),
    weight: z.number().nullish(),
    tags: z.array(z.string()).nullish(),
    cgHierarchy: z.array(z.string()).nullish(),
    /** @deprecated use cgHierarchy to look up metadata */
    cgIcon: z.string().nullish(),
    /** @deprecated use cgHierarchy to look up metadata */
    cgLocalKey: z.string().nullish(),
    flags: z.array(Flag.or(OtherString)).nullish(),
    deposit: z.number().nullish(),
    search: z.boolean().nullish(),
    active: z.boolean().nullish(),
    disableAddLocalKey: DisableAddLocalKey.nullish(),
    capacity: z.number().nullish(),
    positions: z.array(AssignedShelf).nullish(),
    shelfNumbers: z.array(z.number()).nullish(),
    commodityGroupId: z.string().nullish(),
    commodityGroupLongId: z.string().nullish(),
    labels: ResolvedLabel.array().nullish(),
    assignedLabels: z.array(AssignedLabel).nullish(),
    score: z.coerce.number().nullish(),
    salesLimitQty: z.coerce.number().nullish(),
    age: z.coerce.number().nullish(),
    nutrition: ProductNutritionInfo.nullish(),
    isWeightProduct: z.boolean().nullish(),
    releasedOn: z.coerce.date().nullish(),
    deletedOn: z.coerce.date().nullish(),
    deletedOnReason: z
        .object({
            processingSource: z.string(),
            localKey: z.string().nullish(),
            hint: z.string().nullish(),
        })
        .nullish(),
});
export type UiProduct = z.infer<typeof UiProduct>;

export const AdUiProduct = z.object({
    _id: z.string().optional(),
    itemId: z.string(),
    rootItemId: z.string(),
    brand: z.string().nullish(),
    name: z.string(),
    storeGuid: z.string(),
    gtins: z.array(z.string()),
    gtin: z.string().nullish(),
    cgHierarchy: z.array(z.string()).nullish(),
    flags: z.array(Flag.or(OtherString)).nullish(),
    commodityGroupId: z.string().nullish(),
    commodityGroupLongId: z.string().nullish(),
});

export type AdUiProduct = z.infer<typeof AdUiProduct>;
